// 1. TimelineHeader Component
// src/components/Timeline/TimelineHeader.tsx
import React from 'react';
import {
   Box,
   Typography,
   Button,
   Chip,
   Paper,
   useMediaQuery,
   useTheme,
   AvatarGroup,
   Avatar,
} from '@mui/material';
import {
   Today as TodayIcon,
   History as HistoryIcon,
   FlightTakeoff as FlightTakeoffIcon,
   ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { TripCollaborator, TimelineViewType } from './types';

interface TimelineHeaderProps {
   canUserAccess: (role: string[]) => any;
   startDate: Date;
   endDate: Date;
   tripName?: string;
   tripStatus: { label: string; color: string };
   isPastTrip: boolean;
   collaborators: TripCollaborator[];
   view: string;
   onViewChange: (view: string) => void;
   onShowCollaborators: () => void;
}

const TimelineHeader: React.FC<TimelineHeaderProps> = ({
   startDate,
   endDate,
   tripName = 'Trip',
   tripStatus,
   isPastTrip,
   collaborators,
   view,
   onViewChange,
   onShowCollaborators,
   canUserAccess,
}) => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

   return (
      <>
         <Box
            sx={{
               mb: 3,
               display: 'flex',
               flexDirection: { xs: 'column', sm: 'row' },
               justifyContent: 'space-between',
               alignItems: { xs: 'flex-start', sm: 'center' },
               gap: 2,
            }}
         >
            <Box>
               <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant='h6'>{tripName} Timeline</Typography>
                  <Chip
                     label={tripStatus.label}
                     color={tripStatus.color as any}
                     size='small'
                     icon={
                        isPastTrip ? (
                           <HistoryIcon />
                        ) : tripStatus.label === 'Active Trip' ? (
                           <TodayIcon />
                        ) : (
                           <FlightTakeoffIcon />
                        )
                     }
                  />
               </Box>
               <Typography variant='body2' color='text.secondary'>
                  {format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}
               </Typography>
            </Box>

            {collaborators.length > 0 && canUserAccess(['owner']).canAccess && (
               <Box
                  sx={{
                     display: 'flex',
                     alignItems: 'center',
                     p: 1,
                     borderRadius: 1,
                     cursor: 'pointer',
                     '&:hover': { bgcolor: 'action.hover' },
                     border: '1px solid',
                     borderColor: 'divider',
                  }}
                  onClick={onShowCollaborators}
               >
                  <AvatarGroup max={3} sx={{ mr: 1 }}>
                     {collaborators.map((collaborator) => (
                        <Avatar
                           key={collaborator?.id}
                           src={collaborator?.avatar}
                           alt={collaborator?.name}
                           sx={{ width: 30, height: 30 }}
                        >
                           {collaborator?.name?.charAt(0)}
                        </Avatar>
                     ))}
                  </AvatarGroup>
                  <Typography variant='body2'>
                     {collaborators.length > 1
                        ? `Shared with ${collaborators.length} people`
                        : 'Shared with 1 person'}
                  </Typography>
                  <ExpandMoreIcon fontSize='small' sx={{ ml: 1 }} />
               </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
               <Button
                  variant={view === 'feed' ? 'contained' : 'outlined'}
                  size='small'
                  onClick={() => onViewChange('feed')}
               >
                  Feed
               </Button>
               <Button
                  variant={view === 'day' ? 'contained' : 'outlined'}
                  size='small'
                  onClick={() => onViewChange('day')}
               >
                  Days
               </Button>
               <Button
                  variant={view === 'timeline' ? 'contained' : 'outlined'}
                  size='small'
                  onClick={() => onViewChange('timeline')}
               >
                  Timeline
               </Button>
            </Box>
         </Box>

         {/* Past trip notice banner */}
         {isPastTrip && (
            <Paper
               elevation={0}
               sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: 'grey.100',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
               }}
            >
               <HistoryIcon color='action' sx={{ mr: 2 }} />
               <Box sx={{ flex: 1 }}>
                  <Typography variant='subtitle2'>Past Trip</Typography>
                  <Typography variant='body2' color='text.secondary'>
                     This trip has ended. You can view memories and comments, but editing is
                     disabled.
                  </Typography>
               </Box>
            </Paper>
         )}
      </>
   );
};

export default TimelineHeader;
